import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import ImageUploader from "@app/scene/settings/component/common/ImageUploader";
import GeneralSettings from "@app/scene/settings/type/GeneralSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getOrganizationId, getOrganizationName, getOrganizationSettings } from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { USER_IMAGE_FILE_UPLOAD_LIMIT } from "@app/util/consts";
import { complementFileUrl } from "@app/util/file";
import useSetting from "@app/util/hook/useSetting";

import MainFields from "./component/MainFields";
import styles from "./General.module.scss";

interface Props {
  form: FormInstance<GeneralSettings>;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationName?: string;
  organizationSettings: Organization["settings"];
  setDirty: () => void;
  unsetDirty: () => void;
}

const General: React.FC<Props> = ({
  form,
  onUpdateOrganization,
  organizationId,
  organizationName,
  organizationSettings,
  setDirty,
  unsetDirty,
}) => {
  const intl = useIntl();

  const handleSetDirty = useCallback(() => setDirty(), [setDirty]);

  const currencySetting = useSetting(organizationSettings, OrganizationSetting.CURRENCY);
  const distanceSetting = useSetting(organizationSettings, OrganizationSetting.DISTANCE_UNIT);
  const logoSetting = useSetting(organizationSettings, OrganizationSetting.LOGO);
  const timezoneSetting = useSetting(organizationSettings, OrganizationSetting.TIMEZONE);

  const handleSubmit = useCallback(() => {
    const { currency, distance, logo, name, timezone } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        },
        organization: { id: organizationId, name },
        settings: [
          currencySetting && currency ? { ...currencySetting, value: currency } : undefined,
          distanceSetting && distance ? { ...distanceSetting, value: distance } : undefined,
          logoSetting ? { ...logoSetting, value: logo?.publicURI || "" } : undefined,
          timezoneSetting && timezone ? { ...timezoneSetting, value: timezone } : undefined,
        ].filter(isDefined),
      });
    }
  }, [
    currencySetting,
    distanceSetting,
    form,
    intl,
    logoSetting,
    organizationId,
    timezoneSetting,
    onUpdateOrganization,
    unsetDirty,
  ]);
  return (
    <ExtendedAnalytics keyPath={["General"]}>
      <Form
        form={form}
        initialValues={{
          currency: currencySetting?.value,
          distance: distanceSetting?.value,
          logo: logoSetting?.value
            ? { publicURI: complementFileUrl(logoSetting?.value) }
            : undefined,
          name: organizationName,
          timezone: timezoneSetting?.value,
        }}
        onFieldsChange={handleSetDirty}
        onFinish={handleSubmit}
      >
        <Row gutter={[0, layout.spacerL]}>
          <Col sm={{ span: 24 }} xl={{ span: 12 }}>
            <MainFields />
          </Col>
        </Row>
        <Row gutter={[0, layout.spacerL]}>
          <Col sm={{ span: 24 }} xl={{ span: 16 }}>
            <div className={styles.logoLabelContainer}>
              <h3 className={styles.logoLabel}>
                {intl.formatMessage({
                  id: "settings.organizationDetails.logo.label",
                  defaultMessage: "Logo",
                  description: "Logo label of settings organization details",
                })}
              </h3>
              <p className={styles.logoDescription}>
                {intl.formatMessage({
                  id: "settings.organizationDetails.logo.description",
                  defaultMessage:
                    "Add a logo to white-label MMC for your organization. Your logo will appear in the top left corner of your screen.",
                  description: "Logo description of settings organization details",
                })}
              </p>
            </div>
            <ExtendedAnalytics keyPath={["Org Logo"]}>
              <Form.Item name="logo" valuePropName="image">
                <ImageUploader
                  aspect={10 / 3}
                  imageContainerClassName={styles.squareContainer}
                  noImageText={intl.formatMessage({
                    id: "settings.organizationDetails.imageText",
                    defaultMessage: "Add your logo",
                    description: "Image text of settings organization details page",
                  })}
                  sizeLimit={USER_IMAGE_FILE_UPLOAD_LIMIT}
                />
              </Form.Item>
            </ExtendedAnalytics>
          </Col>
        </Row>
      </Form>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  organizationId: getOrganizationId(state),
  organizationName: getOrganizationName(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(General);
